import { isIOS, isIPadOS, isMacSafari, isPWA } from '@/core/helpers/ui.helper';
import { computed, ref } from 'vue';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const deferredPrompt = ref<BeforeInstallPromptEvent | null>(null);
const showIOSInstructions = ref(false);
let listenerRegistered = false;

// Safari never fires beforeinstallprompt, so it gets written steps instead of a prompt.
const needsManualInstall = computed(() => isIOS.value || isIPadOS.value || isMacSafari.value);

export function useInstallPrompt() {
  const canInstall = computed(() => {
    if (isPWA.value) return false;
    if (needsManualInstall.value) return true;
    return !!deferredPrompt.value;
  });

  function init() {
    if (isPWA.value || listenerRegistered) return;
    listenerRegistered = true;

    window.addEventListener('beforeinstallprompt', (e: Event) => {
      e.preventDefault();
      deferredPrompt.value = e as BeforeInstallPromptEvent;
    });

    window.addEventListener('appinstalled', () => {
      deferredPrompt.value = null;
    });
  }

  async function install(): Promise<boolean> {
    if (needsManualInstall.value) {
      showIOSInstructions.value = !showIOSInstructions.value;
      return false;
    }

    if (!deferredPrompt.value) return false;
    await deferredPrompt.value.prompt();
    const { outcome } = await deferredPrompt.value.userChoice;
    deferredPrompt.value = null;
    return outcome === 'accepted';
  }

  function dismissIOSInstructions() {
    showIOSInstructions.value = false;
  }

  return { canInstall, showIOSInstructions, init, install, dismissIOSInstructions };
}
